

import { Check, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const CelebrationButton_ = ({ onClick, isSubmitting, children, className, isSuccess=false }) => {
  const buttonRef = useRef(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [confetti, setConfetti] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const audioRef = useRef(null);
  const trackClick = useRef(false);


  // Create pop sound using Web Audio API
  const playPopSound = useCallback(() => {
    try {
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();

      // Create oscillator for pop sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        400,
        audioContext.currentTime + 0.1,
      );

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.15,
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);

      // Second pop for richness
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.setValueAtTime(1200, audioContext.currentTime);
        osc2.frequency.exponentialRampToValueAtTime(
          600,
          audioContext.currentTime + 0.08,
        );
        gain2.gain.setValueAtTime(0.2, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.1,
        );
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.1);
      }, 50);
    } catch (e) {
      console.log("Audio not supported");
    }
  }, []);

  // Generate confetti particles
  const generateConfetti = useCallback(() => {
    const colors = [
      "#22c55e",
      "#ffffff",
      "#86efac",
      "#4ade80",
      "#10b981",
      "#34d399",
    ];
    const particles = [];

    for (let i = 0; i < 30; i++) {
      const angle = Math.random() * 360 * (Math.PI / 180);
      const velocity = 4 + Math.random() * 6;
      const size = 4 + Math.random() * 6;

      particles.push({
        id: i,
        x: 0,
        y: 0,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity - 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: size,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 20,
        opacity: 1,
        shape: Math.random() > 0.5 ? "circle" : "square",
      });
    }
    return particles;
  }, []);

  const triggerCelebration = useCallback(async () => {
    // Generate confetti
    setConfetti(generateConfetti());
    setShowCelebration(true);

    // Play sound
    playPopSound();

    // Show success message after a small delay
    setTimeout(() => {
      setShowSuccess(true);
    }, 200);

    // Clean up after animation
    setTimeout(() => {
      setShowCelebration(false);
      setShowSuccess(false);
      setConfetti([]);
    }, 2000);
  }, [generateConfetti, playPopSound]);

  const handleClick = async (e) => {
    if (isSubmitting) return;

    // Call the original onClick handler
    trackClick.current = true;
     onClick(e);
  };

  useEffect(()=>{
       if(!isSuccess) return;
       if(!trackClick.current) return;
          trackClick.current = false;
       triggerCelebration();
  },[isSuccess])


  return (
    <div className="relative inline-block">
      {/* Glow effect */}
      <div
        className={`absolute inset-0 rounded-2xl transition-all duration-500 ${
          showCelebration
            ? "shadow-[0_0_40px_10px_rgba(34,197,94,0.5)] animate-pulse"
            : ""
        }`}
      />

      {/* Confetti particles */}
      {showCelebration &&
        confetti.map((particle, index) => (
          <div
            key={particle.id}
            className="absolute pointer-events-none z-50"
            style={{
              left: "50%",
              top: "50%",
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              borderRadius: particle.shape === "circle" ? "50%" : "2px",
              transform: `translate(-50%, -50%)`,
              animation: `confetti-${index % 3} 1.5s ease-out forwards`,
              "--tx": `${particle.vx * 30}px`,
              "--ty": `${particle.vy * 30}px`,
              "--rotation": `${particle.rotation + particle.rotationSpeed * 20}deg`,
            }}
          />
        ))}

      {/* Success message floating above */}
      {/* <div
        className={`absolute -top-16 left-1/2 transform -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-emerald-200 dark:border-emerald-800 transition-all duration-500 ${
          showSuccess
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        style={{
          boxShadow: showSuccess ? "0 4px 20px rgba(34, 197, 94, 0.3)" : "none",
        }}
      >
        <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
          Saved Successfully!
        </span>
      </div> */}

      {/* Main button */}
      <button
        ref={buttonRef}
        onClick={handleClick}
        disabled={isSubmitting}
        className={`${className} relative overflow-visible transition-all duration-300 flex items-center gap-1 align-center ${
          showCelebration ? "scale-95" : ""
        }`}
        style={{
          transform: showCelebration ? "scale(0.98)" : "scale(1)",
        }}
      >
        {children}

        {/* Ripple effect on click */}
        {showCelebration && (
          <span
            className="absolute inset-0 rounded-2xl animate-ping bg-emerald-400 opacity-30"
            style={{ animationDuration: "0.6s", animationIterationCount: "1" }}
          />
        )}
      </button>

      {/* CSS for confetti animation */}
      <style>{`
        @keyframes confetti-0 {
          0% {
            transform: translate(-50%, -50%) rotate(0deg) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty) + 60px)) rotate(var(--rotation)) scale(0.5);
            opacity: 0;
          }
        }
        @keyframes confetti-1 {
          0% {
            transform: translate(-50%, -50%) rotate(0deg) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(calc(-50% + var(--tx) * 1.2), calc(-50% + var(--ty) + 80px)) rotate(var(--rotation)) scale(0.3);
            opacity: 0;
          }
        }
        @keyframes confetti-2 {
          0% {
            transform: translate(-50%, -50%) rotate(0deg) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(calc(-50% + var(--tx) * 0.8), calc(-50% + var(--ty) + 50px)) rotate(var(--rotation)) scale(0.4);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};





const  CelebrationButton = ({onClick,className,label="Save Changes",icon,isSuccess=false,isLoading}) =>{
  const { loader } = useSelector((state) => state.loader);
  const btnLoading = isLoading === undefined?loader:isLoading;
  return(
     <CelebrationButton_ 
            onClick={onClick}
            isSubmitting={btnLoading}
            isSuccess={isSuccess}
            className={className?className:`group px-12 py-5 bg-gradient-to-r from-emerald-500 via-emerald-600 to-cyan-600 hover:from-emerald-600 hover:via-emerald-700 hover:to-cyan-700 text-white rounded-2xl font-black text-lg shadow-2xl shadow-emerald-500/40 flex items-center gap-4 transition-all transform hover:scale-105 } ${btnLoading ? 'opacity-70 cursor-not-allowed' : ''} `}
          >
            {btnLoading  ? (
              <Loader2 className="w-7 h-7 animate-spin" />
            ) : 
            icon?icon:<Save className="w-7 h-7 group-hover:rotate-12 transition-transform" />
              
            }
             {label}
          </CelebrationButton_>
  )
}

export default CelebrationButton;